
import Queue from "queue";
import _ from "lodash";

import * as Constants from "../constants/Constants";
import { SyntheticVoiceProvider } from "../interfaces/SyntheticVoiceProvider";

const ENABLE_LOGGING = true;

const eventListeners = {};
const transactions = {};
const registeredVoiceProviders = {};

const EVENT_TYPES = [
	Constants.EVENT_TYPE_SPEECH_START,
	Constants.EVENT_TYPE_SPEECH_PAUSED,
	Constants.EVENT_TYPE_SPEECH_FINISH,
	Constants.EVENT_TYPE_SPEECH_TRANSACTION_START,
	Constants.EVENT_TYPE_SPEECH_TRANSACTION_PAUSED,
	Constants.EVENT_TYPE_SPEECH_TRANSACTION_FINISH
];

const SYNTHETIC_VOICE_TYPES = [
	Constants.SYNTHETIC_VOICE_TYPE_AUDIO,
	Constants.SYNTHETIC_VOICE_TYPE_TEXT_TO_SPEECH
];

let currentTransactionId = null;
let currentProvider = null;
let currentTransactionSpeechRequestQueue = Queue({
	autostart: true,
	concurrency: 1
});
let transactionQueue = Queue({
	autostart: true,
	concurrency: 1
});
transactionQueue.on("end", (transactionId) => {
	ENABLE_LOGGING && console.log("SyntheticVoice: No more transactions");
	currentProvider = null;
	delete transactions[transactionId];
});
transactionQueue.on("success", (result) => {
	ENABLE_LOGGING && console.log("SyntheticVoice: Transaction FINISHED:", result);
});
transactionQueue.on("error", (err) => {
	ENABLE_LOGGING && console.warn("SyntheticVoice: Transaction FAILED:", err);
});
let stoppingState = false; // Is this currently stopping?


//Utility function, needed because RN (which uses this library) doesn't ship with crypto
function uuid() {
	let d = new Date().getTime();
	let uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		let r = (d + Math.random() * 16) % 16 | 0;
		d = Math.floor(d / 16);
		return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16); // eslint-disable-line no-mixed-operators
	});
	return uuid;
}

function fireEvent(type, ...callbackArgs) {
	if (!isValidEventType(type)) {
		throw new Error(`Unable to add event listener, invalid event type: ${type}`);
	}

	if (eventListeners[type] && _.isArray(eventListeners[type])) {
		eventListeners[type].forEach(callback => {
			if (_.isFunction(callback)) {
				callback(...callbackArgs);
			}
		});
	}
}

function getVoiceProviderFoSpeechRequest(speechRequest) {
	let result = registeredVoiceProviders[speechRequest.type];
	if (!result) {
		throw new Error(`Unable to find registered voice provider for speech request type ${speechRequest.type}`);
	}
	return result;
}

function isValidEventType(type) {
	let validEventType = false;
	ENABLE_LOGGING && console.log("Is valid event type", type, EVENT_TYPES);
	EVENT_TYPES.forEach(eventType => {
		if (eventType === type) {
			validEventType = true;
		}
	});

	if (!validEventType) {
		throw new Error(`Invalid event type: ${type}`);
	}

	return validEventType;
}

export function getAudioSpeechRequest(url, config) {
	return {
		type: Constants.SYNTHETIC_VOICE_TYPE_AUDIO,
		url,
		config
	};
}

export function getTextToSpeechSpeechRequest(text, config) {
	return {
		type: Constants.SYNTHETIC_VOICE_TYPE_TEXT_TO_SPEECH,
		text,
		config
	};
}

export function registerVoiceProvider(type, provider) {
	let validProviderType = false;
	SYNTHETIC_VOICE_TYPES.forEach(voiceType => {
		if (voiceType === type) {
			validProviderType = true;
		}
	});
	if (!validProviderType) {
		throw new Error(`Unable to register voice provider, unknown type ${type}`);
	}
	registeredVoiceProviders[type] = provider;
}

let bufferedSpeechRequests = [];

export async function runBufferedSpeechRequests() {
	stoppingState = false;
	bufferedSpeechRequests.forEach(bufferedSpeechRequest => {
		const { speechRequests, config, metaData, transactionId } = bufferedSpeechRequest;
		speak(speechRequests, config, metaData, transactionId);
	});
}

/**
 * Starts a new transaction with one or more speech requests
 * @param {*} speechRequests 
 * @param {*} config 
 */
export async function speak(speechRequests, config, metaData, transactionId = uuid()) {
	// Will makesure all speech requests are encapsulated under one transaction
	if (stoppingState) {
		bufferedSpeechRequests.push({ speechRequests, config, metaData, transactionId });
		return;
	}

	ENABLE_LOGGING && console.log("SyntheticVoice: speak:", speechRequests, config, transactionId);
	let _speechRequests = [];

	bufferedSpeechRequests.push(speechRequests);

	bufferedSpeechRequests.forEach(bufferedSpeechRequest => {
		if (_.isString(bufferedSpeechRequest)) {
			_speechRequests.push(getTextToSpeechSpeechRequest(bufferedSpeechRequest, config));
		} else if (_.isArray(bufferedSpeechRequest)) {
			_speechRequests = bufferedSpeechRequest;
		} else if (speechRequests instanceof SyntheticVoiceProvider) {
			_speechRequests.push(bufferedSpeechRequest);
		}
	});
	bufferedSpeechRequests = []; // empty it!


	const transaction = {
		transactionId,
		speechRequests: _speechRequests,
		config,
		metaData
	};

	if (!transactions[transactionId]) {
		transactions[transactionId] = transaction;
	}
	ENABLE_LOGGING && console.log("SyntheticVoice: add to transaction queue:", transaction);
	transactionQueue.push(() => handleTransaction(transaction));
}

function handleTransaction(transaction) {

	return new Promise((resolve, reject) => {
		ENABLE_LOGGING && console.log("SyntheticVoice: Handle Transaction:", transaction);
		fireEvent(Constants.EVENT_TYPE_SPEECH_TRANSACTION_START, transaction);
		const { transactionId, speechRequests } = transaction;
		currentTransactionId = transactionId;

		currentTransactionSpeechRequestQueue.removeAllListeners("end");
		currentTransactionSpeechRequestQueue.removeAllListeners("error");

		currentTransactionSpeechRequestQueue.on("end", (err) => {
			ENABLE_LOGGING && console.log("SyntheticVoice: Transaction Done:", currentTransactionId, transaction, err);
			fireEvent(Constants.EVENT_TYPE_SPEECH_TRANSACTION_FINISH, _.assign({}, transaction, { err }));
			currentTransactionId = null;
			resolve(transactionId);
		});

		currentTransactionSpeechRequestQueue.on("error", (err) => {
			ENABLE_LOGGING && console.error("SyntheticVoice: Transaction Failed:", currentTransactionId, err);
			currentTransactionId = null;
			if (err && err.reason === "stopped") {
				fireEvent(Constants.EVENT_TYPE_SPEECH_TRANSACTION_FINISH, transaction);
				resolve(transactionId);
			} else {
				reject(err);
			}
		});
		const n = speechRequests.length;
		// let promises = null;
		for (let i = 0; i < n; i++) {
			try {
				const speechRequest = speechRequests[i];
				// Check to make sure this hasn't been deleted
				currentTransactionSpeechRequestQueue.push(() => handleSpeechRequest(speechRequest, transactionId, Constants, currentTransactionSpeechRequestQueue)); // eslint-disable-line no-loop-func

			} catch (e) {
				console.error("Execution of speech request failed", e);
			}
		}
	});
}
let startedCount = 0;
let lastProviderStarted = null;
function handleSpeechRequest(speechRequest, transactionId, Constants) {
	return new Promise(async (resolve, reject) => { // eslint-disable-line no-async-promise-executor
		ENABLE_LOGGING && console.log("SyntheticVoice: Handle Speech request", transactionId, speechRequest);
		if (!transactions[transactionId] || stoppingState) {
			ENABLE_LOGGING && console.log("SyntheticVoice: speech request skipped (in stopping state)");
			resolve(transactionId);
			return;
		}
		const VoiceProvider = getVoiceProviderFoSpeechRequest(speechRequest);
		const provider = new VoiceProvider();
		provider.addEventListener(Constants.EVENT_TYPE_SPEECH_START, () => {
			ENABLE_LOGGING && console.log("SyntheticVoice: speech request started " + ++startedCount + " (callback)", transactionId, speechRequest);
		});
		const finalizeFunc = () => {
			ENABLE_LOGGING && console.log("SyntheticVoice: speech request finalize");
			delete transactions[transactionId];
			currentProvider = null;
			currentTransactionSpeechRequestQueue.end({
				reason: "stopped"
			});
			transactionQueue.end();
			stoppingState = false;
			runBufferedSpeechRequests();
			reject({
				reason: "stopped"
			});
		};
		provider.addEventListener(Constants.EVENT_TYPE_SPEECH_FINISH, (err) => {

			if (stoppingState || err) {
				ENABLE_LOGGING && console.log("SyntheticVoice: speech request finished (STOPPED)", transactionId, err, speechRequest);


				if (stoppingState && err) {
					setTimeout(finalizeFunc, 1000);
				} else {
					finalizeFunc();
				}

			} else {
				ENABLE_LOGGING && console.log("SyntheticVoice: speech request finished", transactionId, speechRequest);
				resolve();
			}
		});
		currentProvider = provider;
		try {
			if (!transactions[transactionId] || stoppingState) {
				finalizeFunc();
			} else {
				lastProviderStarted = provider;
				await provider.start(speechRequest);
			}
		} catch (_e) {
			const { fallback } = speechRequest;
			if (fallback) {
				console.warn(_e);
				// try the fallback before rejecting
				ENABLE_LOGGING && console.log("SyntheticVoice: Attempting fallback", fallback);
				try {
					await handleSpeechRequest(fallback, transactionId, Constants);
					resolve(fallback); // Resolve the executed speechRequest
				} catch (_e1) {
					console.error(_e1);
					reject(_e1);
				}
			} else {
				console.error(_e, speechRequest);
				reject(_e);
			}
		}
	});
}

export function addEventListener(type, callback) {

	if (!isValidEventType(type)) {
		throw new Error(`Unable to add event listener, invalid event type: ${type}`);
	}

	if (!eventListeners[type]) {
		eventListeners[type] = [];
	}

	eventListeners[type].push(callback);
}

export function removeEventListener(type, callback) {

	if (!isValidEventType(type)) {
		throw new Error(`Unable to remove event listener, invalid event type: ${type}`);
	}

	let _idx = -1;
	if (eventListeners[type] && _.isArray(eventListeners[type])) {
		eventListeners[type].forEach((cb, idx) => {
			if (cb === callback) {
				_idx = idx;
			}
		});
	}

	if (_idx > -1) {
		eventListeners[type].splice(_idx, 1);
	} else {
		console.warn("Unable to remove callback, unable to find callback for registered evenbt listener");
	}

}

export function getTextToSpeechProvider() {
	return registeredVoiceProviders[Constants.SYNTHETIC_VOICE_TYPE_TEXT_TO_SPEECH];
}


export function getAudioProvider() {
	return registeredVoiceProviders[Constants.SYNTHETIC_VOICE_TYPE_AUDIO];
}
export function getVoices() {
	const ttsProvider = getTextToSpeechProvider();
	if (ttsProvider) {
		return ttsProvider.getVoices();
	} else {
		return Promise.resolve([]);
	}
}
export function setDefaultVoice(id) {
	const ttsProvider = getTextToSpeechProvider();
	if (ttsProvider) {
		ttsProvider.setDefaultVoice(id);
	}
}

export function setDefaultRate(speed) {
	const ttsProvider = getTextToSpeechProvider();
	if (ttsProvider) {
		ttsProvider.setDefaultSpeed(speed);
	}
	const audioProvider = getAudioProvider();
	if (audioProvider) {
		audioProvider.setDefaultSpeed(speed);
	}
}

export function stop() {
	ENABLE_LOGGING && console.log("SyntheticVoice: Stop requested ---");
	if (currentProvider || currentTransactionId || transactionQueue.length > 0) {
		if (currentProvider) {
			stoppingState = true;
			ENABLE_LOGGING && console.log("SyntheticVoice: Stop requested: Provider exists, calling stop.");
			currentProvider.stop();
		} else if (lastProviderStarted) {
			try {
				lastProviderStarted.stop();
				lastProviderStarted = null;
				ENABLE_LOGGING && console.log("SyntheticVoice: Unable to stop --- falling back to last known provider reference", startedCount, lastProviderStarted, currentProvider, currentTransactionId, transactionQueue.length, currentTransactionSpeechRequestQueue.length);
			} catch (e) {
				ENABLE_LOGGING && console.log("SyntheticVoice: Unable to stop --- falling back to last known provider reference, error occurred while trying to attempt stop", e, startedCount, lastProviderStarted, currentProvider, currentTransactionId, transactionQueue.length, currentTransactionSpeechRequestQueue.length);
			}
		} else {
			stoppingState = true;
			ENABLE_LOGGING && console.log("SyntheticVoice: Stop requested: No provider, clear manually.");
			if (currentTransactionId) {
				delete transactions[currentTransactionId];
			}
			currentProvider = null;
			currentTransactionSpeechRequestQueue.end({
				reason: "stopped"
			});
			transactionQueue.end();
			stoppingState = false;
		}
	} else {
		try {
			ENABLE_LOGGING && console.log("SyntheticVoice: Unable to stop --- falling back to last known provider reference", startedCount, lastProviderStarted, currentProvider, currentTransactionId, transactionQueue.length, currentTransactionSpeechRequestQueue.length);
			lastProviderStarted.stop();
			lastProviderStarted = null;
		} catch (e) {
			ENABLE_LOGGING && console.log("SyntheticVoice: Unable to stop --- falling back to last known provider reference, error occurred while trying to attempt stop", e, startedCount, lastProviderStarted, currentProvider, currentTransactionId, transactionQueue.length, currentTransactionSpeechRequestQueue.length);
		}
	}
}

export function isSpeaking() {
	return transactionQueue.length !== 0 || currentProvider;
}